import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-sans cursor-pointer transition-brand transition-[background-color,color,opacity,transform,box-shadow] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary:
          "rounded-full bg-brand-650 text-paper-0 font-semibold hover:bg-brand-700 focus-visible:ring-brand-700 dark:bg-brand-400 dark:text-on-brand-400 dark:hover:bg-brand-300 dark:focus-visible:ring-brand-400 min-h-[44px] min-w-[44px]",
        secondary:
          "rounded-full bg-transparent text-brand-700 font-medium hover:text-brand-800 min-h-[44px] min-w-[44px] [&_svg]:transition-transform [&_svg]:duration-150 hover:[&_svg]:translate-x-1",
        "ghost-link":
          "px-0 h-auto min-h-[44px] bg-transparent text-body-sm text-ink-600 hover:text-ink-900 dark:text-ondark-muted dark:hover:text-paper-0 [&_svg]:transition-transform [&_svg]:duration-150 hover:[&_svg]:translate-x-1",
        default: "rounded-full bg-primary text-primary-foreground hover:opacity-90 min-h-[44px]",
        destructive:
          "rounded-full bg-destructive text-destructive-foreground hover:opacity-90 min-h-[44px]",
        outline: "rounded-full border border-input bg-background hover:bg-secondary min-h-[44px]",
        ghost: "rounded-full hover:bg-secondary min-h-[44px]",
        link: "text-primary underline-offset-4 hover:underline min-h-[44px]",
      },
      size: {
        default: "h-14 px-8 text-body",
        sm: "h-12 rounded-full px-6 text-body-sm",
        lg: "h-16 rounded-full px-9 text-body-lg",
        icon: "h-11 w-11 rounded-full",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
